import { type FC, useCallback, useEffect, useRef } from "react";
import useEscapeKey from "src/ui/hooks/useEscapeKey";
import { useNavigate, useMatch } from "react-router-dom";
import type { ConversationResponse } from "@9amhealth/openapi";
import { useBlocNext } from "@blac/react";
import translate from "src/lib/translate";
import ConversationListCubit from "./state/ConversationListCubit";
import LeapChatRouterCubit from "./state/LeapChatRouterCubit";
import { formatRelativeTime } from "./state/utils";
import { TourGuideAnchor } from "@9amhealth/shared";
import { ChatTourAnchor } from "./chatTourConstants";
import { HeaderIconButton, NewConversationIcon } from "./NavButton";
import {
  DrawerRoot,
  DrawerPanel,
  DrawerBackdrop,
  DrawerShift,
  DrawerHeader,
  ArchiveList,
  ArchiveItem,
  DRAWER_DESKTOP_BREAKPOINT,
  ArchiveListSectionTitle
} from "./leapChat.styles";
import { useMediaQuery } from "@react-hooks-library/core";

const ConversationItem: FC<{
  conv: ConversationResponse;
  activeConversationId: string | null;
  onClick: (id: string) => void;
}> = ({ conv, activeConversationId, onClick }) => {
  const escalated = ConversationListCubit.isEscalated(conv);

  return (
    <ArchiveItem
      key={conv.id}
      data-active={conv.id === activeConversationId ? "" : undefined}
      data-escalated={escalated ? "" : undefined}
      onClick={() => onClick(conv.id)}
    >
      <span className="archive-item-title">
        {conv.title ?? translate("leapChat.archive.untitled")}
      </span>
      <span className="archive-item-meta">
        {escalated && (
          <span className="archive-item-badge">
            {translate("leapChat.archive.waitingForReply")}
          </span>
        )}
        <span className="archive-item-date">
          {formatRelativeTime(conv.createdAt)}
        </span>
      </span>
      {conv.unreadMessageCount > 0 && (
        <span className="archive-item-unread-dot" />
      )}
    </ArchiveItem>
  );
};

const ChatDrawerLayout: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [{ isDrawerOpen, activeConversations, historyConversations }, cubit] =
    useBlocNext(ConversationListCubit);
  const [{ conversationHistoryId }] = useBlocNext(LeapChatRouterCubit);
  const navigate = useNavigate();
  const conversationMatch = useMatch("/app/chat/conversation/:id");
  const activeConversationId = conversationMatch?.params.id ?? null;

  const isMobile = useMediaQuery(`(max-width: ${DRAWER_DESKTOP_BREAKPOINT}px)`);

  useEffect(() => {
    cubit.isMobile = isMobile;
  }, [isMobile, cubit]);

  useEffect(() => {
    void cubit.refresh();
  }, []);

  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (isMobile) {
      returnFocusRef.current = document.activeElement;
      requestAnimationFrame(() => panelRef.current?.focus());
    } else if (returnFocusRef.current) {
      (returnFocusRef.current as HTMLElement).focus();
      returnFocusRef.current = null;
    }
  }, [isDrawerOpen, isMobile]);

  useEscapeKey(cubit.closeDrawer, isDrawerOpen && isMobile);

  const handleConversationClick = useCallback(
    (id: string) => {
      cubit.closeDrawer();
      navigate(`/app/chat/conversation/${id}`);
    },
    [cubit, navigate]
  );

  const handleNewConversation = useCallback(() => {
    cubit.closeDrawer();
    navigate("/app/chat/new", { state: { autoFocus: true } });
  }, [cubit, navigate]);

  return (
    <DrawerRoot>
      <DrawerPanel
        ref={panelRef}
        tabIndex={-1}
        data-open={isDrawerOpen ? "" : undefined}
        role={isMobile ? "dialog" : "complementary"}
        aria-modal={isMobile && isDrawerOpen ? true : undefined}
        aria-label={translate("leapChat.archive.title")}
      >
        <DrawerHeader>
          <HeaderIconButton
            onClick={handleNewConversation}
            aria-label={translate("leapChat.aria.newConversation")}
          >
            <NewConversationIcon />
          </HeaderIconButton>
        </DrawerHeader>
        {activeConversations.length > 0 && (
          <ArchiveList>
            <ArchiveListSectionTitle>
              {translate("leap.archive.section.title_active")}
            </ArchiveListSectionTitle>
            {activeConversations.map((conv) => {
              const item = (
                <ConversationItem
                  key={conv.id}
                  conv={conv}
                  activeConversationId={activeConversationId}
                  onClick={handleConversationClick}
                />
              );
              if (conv.id === conversationHistoryId) {
                return (
                  <TourGuideAnchor
                    key={conv.id}
                    name={ChatTourAnchor.HISTORY_ITEM}
                  >
                    {item}
                  </TourGuideAnchor>
                );
              }
              return item;
            })}
          </ArchiveList>
        )}
        {historyConversations.length > 0 && (
          <ArchiveList>
            <ArchiveListSectionTitle>
              {translate("leap.archive.section.title_history")}
            </ArchiveListSectionTitle>
            {historyConversations.map((conv) => {
              const item = (
                <ConversationItem
                  key={conv.id}
                  conv={conv}
                  activeConversationId={activeConversationId}
                  onClick={handleConversationClick}
                />
              );
              if (conv.id === conversationHistoryId) {
                return (
                  <TourGuideAnchor
                    key={conv.id}
                    name={ChatTourAnchor.HISTORY_ITEM}
                  >
                    {item}
                  </TourGuideAnchor>
                );
              }
              return item;
            })}
          </ArchiveList>
        )}
      </DrawerPanel>

      <DrawerBackdrop
        aria-hidden="true"
        data-open={isDrawerOpen ? "" : undefined}
        onClick={() => cubit.closeDrawer()}
      />

      <DrawerShift
        data-open={isDrawerOpen ? "" : undefined}
        inert={isDrawerOpen && isMobile ? true : undefined}
      >
        {children}
      </DrawerShift>
    </DrawerRoot>
  );
};

export default ChatDrawerLayout;
